import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { getRentings, updateRenting } from "../../../api/rentingsAPI";
import { updateListing } from "../../../api/listingsAPI";
import { updateVehicle } from "../../../api/vehicleAPI";
import { performActionable } from "../../../api/dynamicAPI.js";
import {
  RentingStatus,
  SearchType,
  SeasonPassStatus,
} from "../../../app/constants";
import { createAppointment } from "../../../api/appointmentAPI";
import { addFilter } from "../../../app/utils.js";
import {
  getSeasonPasses,
  getSeasonPassWorkflowTasks,
} from "./seasonPassTrackingAPI";

const getErrorMessage = (error) =>
  error.response?.data?.detail ??
  error.response?.data?.message ??
  error.response?.data?.error ??
  error.message;

let latestSeasonPassRequest = 0;

export const trackingSheetSlice = createSlice({
  name: "trackingSheet",
  initialState: {
    rentings: [],
    pagination: {},
    page: 0,
    rowsPerPage: 100,
    sort: "desc",
    sortBy: "startDate",
    selectedId: null,
    selectedRenting: null,
    selectedAppointmentIds: null,
    searchValue: "",
    selectedFilter: {
      parkingAreaName: null,
      status: null,
      operationStatus: null,
    },
    actionUrl: "",
    actionMethod: "",
    activateStartDate: null,
    editRentingModalOpen: false,
    editListingModalOpen: false,
    allocateModalOpen: false,
    deallocateModalOpen: false,
    activateModalOpen: false,
    terminateModalOpen: false,
    createAppointmentModalOpen: false,
    filterModalOpen: false,
    swapParkingModalOpen: false,
    freezeRentingModalOpen: false,
    unfreezeRentingModalOpen: false,
    viewAppointmentModalOpen: false,
    successMessage: "",
    errorMessage: "",
    seasonPasses: [],
    seasonPassPagination: {},
    seasonPassPage: 0,
    seasonPassRowsPerPage: 100,
    seasonPassSort: "desc",
    seasonPassSortBy: "createdAt",
    seasonPassListStatus: "idle",
    seasonPassListError: "",
    seasonPassWorkflowError: "",
    seasonPassStatus: SeasonPassStatus.PAID,
    seasonPassSuccessMessage: "",
    seasonPassErrorMessage: "",
    selectedSeasonPassId: null,
    selectedSeasonPassAction: null,
    seasonPassActionModalOpen: false,
  },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setRentings: (state, action) => {
      state.rentings = action.payload;
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload;
    },
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
    },
    setSelectedRenting: (state, action) => {
      state.selectedRenting = action.payload;
    },
    setAppointmentIds: (state, action) => {
      state.selectedAppointmentIds = action.payload;
    },
    setActionUrl: (state, action) => {
      state.actionUrl = action.payload;
    },
    setActionMethod: (state, action) => {
      state.actionMethod = action.payload;
    },
    setActivateStartDate: (state, action) => {
      state.activateStartDate = action.payload;
    },
    setSelectedFilter: (state, action) => {
      state.selectedFilter = action.payload;
    },
    showEditRentingModal: (state, action) => {
      state.editRentingModalOpen = action.payload;
    },
    showEditListingModal: (state, action) => {
      state.editListingModalOpen = action.payload;
    },
    showAllocateModal: (state, action) => {
      state.allocateModalOpen = action.payload;
    },
    showDeallocateModal: (state, action) => {
      state.deallocateModalOpen = action.payload;
    },
    showActivateModal: (state, action) => {
      state.activateModalOpen = action.payload;
    },
    showTerminateModal: (state, action) => {
      state.terminateModalOpen = action.payload;
    },
    showCreateAppointmentModal: (state, action) => {
      state.createAppointmentModalOpen = action.payload;
    },
    showFilterModal: (state, action) => {
      state.filterModalOpen = action.payload;
    },
    showSwapParkingModal: (state, action) => {
      state.swapParkingModalOpen = action.payload;
    },
    showFreezeRentingModal: (state, action) => {
      state.freezeRentingModalOpen = action.payload;
    },
    showUnfreezeRentingModal: (state, action) => {
      state.unfreezeRentingModalOpen = action.payload;
    },
    showViewAppointmentModal: (state, action) => {
      state.viewAppointmentModalOpen = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
    setSeasonPasses: (state, action) => {
      state.seasonPasses = action.payload;
      if (!action.payload.some((pass) => pass.id === state.selectedSeasonPassId)) {
        state.selectedSeasonPassId = null;
      }
    },
    setSeasonPassPagination: (state, action) => {
      state.seasonPassPagination = action.payload;
    },
    setSeasonPassPage: (state, action) => {
      state.seasonPassPage = action.payload;
      state.selectedSeasonPassId = null;
    },
    setSeasonPassRowsPerPage: (state, action) => {
      state.seasonPassRowsPerPage = action.payload;
      state.selectedSeasonPassId = null;
    },
    setSeasonPassSort: (state, action) => {
      state.seasonPassSort = action.payload;
      state.selectedSeasonPassId = null;
    },
    setSeasonPassSortBy: (state, action) => {
      state.seasonPassSortBy = action.payload;
      state.selectedSeasonPassId = null;
    },
    setSeasonPassListStatus: (state, action) => {
      state.seasonPassListStatus = action.payload;
    },
    setSeasonPassListError: (state, action) => {
      state.seasonPassListError = action.payload;
    },
    setSeasonPassWorkflowError: (state, action) => {
      state.seasonPassWorkflowError = action.payload;
    },
    setSeasonPassStatus: (state, action) => {
      if (state.seasonPassStatus !== action.payload) {
        state.seasonPassPage = 0;
        state.selectedSeasonPassId = null;
      }
      state.seasonPassStatus = action.payload;
    },
    setSeasonPassSuccessMessage: (state, action) => {
      state.seasonPassSuccessMessage = action.payload;
    },
    setSeasonPassErrorMessage: (state, action) => {
      state.seasonPassErrorMessage = action.payload;
    },
    setSelectedSeasonPassId: (state, action) => {
      state.selectedSeasonPassId = action.payload;
    },
    setSelectedSeasonPassAction: (state, action) => {
      state.selectedSeasonPassAction = action.payload;
    },
    showSeasonPassActionModal: (state, action) => {
      state.seasonPassActionModalOpen = action.payload;
      if (!action.payload) state.selectedSeasonPassAction = null;
    },
  },
});

export const {
  setAppointmentIds,
  showViewAppointmentModal,
  showFreezeRentingModal,
  showUnfreezeRentingModal,
  showSwapParkingModal,
  setSelectedFilter,
  showFilterModal,
  setPage,
  setRowsPerPage,
  setPagination,
  setSort,
  setSortBy,
  setSearchValue,
  setRentings,
  setSelectedId,
  setSelectedRenting,
  setActionUrl,
  setActionMethod,
  setActivateStartDate,
  showEditRentingModal,
  showEditListingModal,
  showAllocateModal,
  showDeallocateModal,
  showActivateModal,
  showTerminateModal,
  showCreateAppointmentModal,
  setSuccessMessage,
  setErrorMessage,
  setSeasonPasses,
  setSeasonPassPagination,
  setSeasonPassPage,
  setSeasonPassRowsPerPage,
  setSeasonPassSort,
  setSeasonPassSortBy,
  setSeasonPassListStatus,
  setSeasonPassListError,
  setSeasonPassWorkflowError,
  setSeasonPassStatus,
  setSeasonPassSuccessMessage,
  setSeasonPassErrorMessage,
  setSelectedSeasonPassId,
  setSelectedSeasonPassAction,
  showSeasonPassActionModal,
} = trackingSheetSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.vendor.value)`
export const selectState = (state) => state.trackingSheet;

export const handleGetRentings = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    let filterParamsArray = [];
    const { selectedFilter } = state.trackingSheet;

    addFilter(
      filterParamsArray,
      "requestedParkingArea.name",
      selectedFilter.parkingAreaName
        ? encodeURIComponent(selectedFilter.parkingAreaName)
        : null
    );
    addFilter(filterParamsArray, "status", selectedFilter.status);
    addFilter(
      filterParamsArray,
      "additionalInformation.operationStatus",
      selectedFilter.operationStatus
    );
    let filterParams = filterParamsArray.join(",");

    const response = await getRentings(
      state.trackingSheet.page,
      state.trackingSheet.rowsPerPage,
      state.trackingSheet.sort,
      state.trackingSheet.sortBy,
      `${RentingStatus.BOOKED},${RentingStatus.PRE_ALLOCATED},${RentingStatus.ALLOCATED},${RentingStatus.PAID},${RentingStatus.PRE_ACTIVE},${RentingStatus.INACTIVE}`,
      state.trackingSheet.searchValue,
      SearchType.ENQUIRY,
      filterParams
    );
    const { content, ...pagination } = response.data;

    dispatch(setPagination(pagination));
    dispatch(setRentings(content));
  } catch (error) {
    alert(error.message);
  } finally {
    dispatch(hideLoading());
  }
};

export const handleGetSeasonPasses = (status, { background = false, includeWorkflow = true } = {}) => async (dispatch, getState) => {
  const requestId = ++latestSeasonPassRequest;
  if (!background) {
    dispatch(showLoading());
    dispatch(setSeasonPassListStatus("loading"));
  }
  dispatch(setSeasonPassListError(""));
  if (includeWorkflow) dispatch(setSeasonPassWorkflowError(""));
  if (status) dispatch(setSeasonPassStatus(status));
  const { trackingSheet } = getState();
  const requestedStatus = trackingSheet.seasonPassStatus;

  try {
    const [passResult, workflowResult] = await Promise.allSettled([
      getSeasonPasses(
        trackingSheet.seasonPassPage,
        trackingSheet.seasonPassRowsPerPage,
        trackingSheet.seasonPassSort,
        trackingSheet.seasonPassSortBy,
        requestedStatus
      ),
      includeWorkflow ? getSeasonPassWorkflowTasks() : Promise.resolve({ data: [] }),
    ]);
    if (requestId !== latestSeasonPassRequest || getState().trackingSheet.seasonPassStatus !== requestedStatus) return;
    if (passResult.status === "rejected") throw passResult.reason;

    const response = passResult.value;
    const workflowResponse = workflowResult.status === "fulfilled" ? workflowResult.value : { data: [] };
    if (includeWorkflow && workflowResult.status === "rejected") {
      dispatch(setSeasonPassWorkflowError(getErrorMessage(workflowResult.reason)));
    }
    const { content, ...pagination } = response.data;
    const tasksBySeasonPassId = (workflowResponse.data ?? []).reduce(
      (tasks, task) => {
        if (!task.refId || !task.bpmnId?.startsWith("season_pass")) {
          return tasks;
        }

        const existingTask = tasks[task.refId];
        if (
          !existingTask ||
          task.bpmnId === "season_pass_termination_process"
        ) {
          tasks[task.refId] = task;
        }
        return tasks;
      },
      {}
    );
    const passesWithActionables = content.map((seasonPass) => {
      const task = tasksBySeasonPassId[seasonPass.id];
      return {
        ...seasonPass,
        actionables: (task?.actions ?? []).map((title) => ({
          title,
          type: "TASK",
          dto: task.dto,
          bpmnId: task.bpmnId,
          taskTitle: task.title,
          errorCode: task.errorCode,
          errorMessage: task.errorMessage,
          url: `/workflow/tasks/${task.id}/${encodeURIComponent(title)}`,
        })),
      };
    });

    dispatch(setSeasonPasses(passesWithActionables));
    dispatch(setSeasonPassPagination(pagination));
    dispatch(setSeasonPassListStatus("succeeded"));
  } catch (error) {
    dispatch(setSeasonPassListError(getErrorMessage(error)));
    dispatch(setSeasonPassListStatus("failed"));
  } finally {
    if (!background) dispatch(hideLoading());
  }
};

export const handlePerformSeasonPassAction =
  (payload = null) => async (dispatch, getState) => {
    dispatch(showLoading());
    const { selectedSeasonPassAction } = getState().trackingSheet;

    try {
      await performActionable(
        selectedSeasonPassAction.url,
        selectedSeasonPassAction.type,
        payload
      );
      dispatch(showSeasonPassActionModal(false));
      dispatch(
        setSeasonPassSuccessMessage(
          `Successfully performed ${selectedSeasonPassAction.title.toLowerCase()} action!`
        )
      );
      dispatch(setSelectedSeasonPassId(null));
      dispatch(handleGetSeasonPasses(getState().trackingSheet.seasonPassStatus));
    } catch (error) {
      dispatch(setSeasonPassErrorMessage(getErrorMessage(error)));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleUpdateRenting = (renting) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await updateRenting(state.trackingSheet.selectedId, renting);
    dispatch(showEditRentingModal(false));
    dispatch(setSuccessMessage("Successfully updated renting!"));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleUpdateVehicle = (id, payload) => async (dispatch) => {
  dispatch(showLoading());

  try {
    await updateVehicle(id, payload);
    dispatch(showEditRentingModal(false));
    dispatch(setSuccessMessage("Successfully updated vehicle!"));
  } catch (error) {
    dispatch(setErrorMessage("Failed to update vehicle: " + error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleUpdateListing = (listing) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await updateListing(
      state.trackingSheet.selectedRenting.listing.id,
      listing
    );
    dispatch(showEditListingModal(false));
    dispatch(
      setSuccessMessage("Successfully updated tracking sheet's listing!")
    );
    dispatch(handleGetRentings());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleAllocateRenting =
  (payload) => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      await performActionable(
        state.trackingSheet.actionUrl,
        state.trackingSheet.actionMethod,
        payload
      );
      dispatch(showAllocateModal(false));
      dispatch(setSuccessMessage("Successfully allocated renting!"));
      dispatch(handleGetRentings());
      dispatch(setSelectedId(null));
    } catch (error) {
      dispatch(setErrorMessage(error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleDeallocateRenting = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await performActionable(
      state.trackingSheet.actionUrl,
      state.trackingSheet.actionMethod,
      null
    );
    dispatch(showDeallocateModal(false));
    dispatch(setSuccessMessage("Successfully sent deallocate request!"));
    dispatch(handleGetRentings());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleActivateRenting =
  (payload) => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      await performActionable(
        state.trackingSheet.actionUrl,
        state.trackingSheet.actionMethod,
        payload
      );
      dispatch(showActivateModal(false));
      dispatch(setSuccessMessage("Successfully activated renting!"));
      dispatch(handleGetRentings());
      dispatch(setSelectedId(null));
    } catch (error) {
      dispatch(setErrorMessage(error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleTerminateRenting = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await performActionable(
      state.trackingSheet.actionUrl,
      state.trackingSheet.actionMethod,
      null
    );
    dispatch(showTerminateModal(false));
    dispatch(setSuccessMessage("Successfully sent terminate request!"));
    dispatch(handleGetRentings());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleCreateAppointment =
  (appointment) => async (dispatch, getState) => {
    dispatch(showLoading());

    try {
      await createAppointment(appointment);
      dispatch(showCreateAppointmentModal(false));
      dispatch(setSuccessMessage("Successfully create appointment!"));
      dispatch(handleGetRentings());
      dispatch(setSelectedId(null));
    } catch (error) {
      dispatch(setErrorMessage(error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

export const handleSwapParking = (payload) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await performActionable(
      state.trackingSheet.actionUrl,
      state.trackingSheet.actionMethod,
      payload
    );
    dispatch(showSwapParkingModal(false));
    dispatch(setSuccessMessage("Successfully Swap Parking Bay!"));
    dispatch(handleGetRentings());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleFreezeRenting = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await performActionable(
      state.trackingSheet.actionUrl,
      state.trackingSheet.actionMethod,
      null
    );
    dispatch(showFreezeRentingModal(false));
    dispatch(setSuccessMessage("Successfully sent freeze request!"));
    dispatch(handleGetRentings());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleUnfreezeRenting = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    await performActionable(
      state.trackingSheet.actionUrl,
      state.trackingSheet.actionMethod,
      null
    );
    dispatch(showUnfreezeRentingModal(false));
    dispatch(setSuccessMessage("Successfully sent unfreeze request!"));
    dispatch(handleGetRentings());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};
export default trackingSheetSlice.reducer;
